import { white } from '@kmns-internal/furikake'
import { DeleteOutlined, MoreHoriz } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import {
  PlaceNode,
  PlaceNodeTypeEnum,
  Template,
  TemplateLayoutTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { Dispatch, FC, MouseEvent, SetStateAction, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import date from 'src/exShared/util/date'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { GridToTableMigrationMenuItem } from 'src/features/gridToTableMigration'
import { semanticColors } from 'src/features/theme/semanticColors'
import { deleteTemplate } from 'src/state/ducks/templateList/actions'
import { RootState } from 'src/state/store'
import { TTemplateWithId, isTemplateWithId } from './Templates'
import { GridTemplateTag, ImproveOptionTag } from './Templates.styled'

const ACTION_COLUMN_WIDTH = '60px'

const TemplateList: FC<{
  selectedTemplateIdsState: [Set<number>, Dispatch<SetStateAction<Set<number>>>]
}> = ({ selectedTemplateIdsState }) => {
  const [selectedTemplateIds, setSelectedTemplateIds] = selectedTemplateIdsState

  const { templates } = useSelector(
    (state: RootState) => state.templateListState,
  )

  const isEveryTemplateSelected = () => {
    if (!templates.length) {
      return false
    }
    return templates.every(
      template =>
        isTemplateWithId(template) && selectedTemplateIds.has(template.id),
    )
  }

  const selectAllTemplates = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const templateIds = templates
        .filter((template): template is TTemplateWithId =>
          isTemplateWithId(template),
        )
        .map(({ id }) => id)
      setSelectedTemplateIds(new Set(templateIds))
    } else {
      setSelectedTemplateIds(new Set([]))
    }
  }

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" sx={{ backgroundColor: white }}>
            <Checkbox
              checked={isEveryTemplateSelected()}
              onChange={selectAllTemplates}
              size="small"
            />
          </TableCell>
          <TableCell
            sx={{ backgroundColor: white, wordBreak: 'break-all' }}
            size="small"
          >
            ひな形名
          </TableCell>
          <TableCell sx={{ backgroundColor: white, width: '20%' }} size="small">
            現場名
          </TableCell>
          <TableCell sx={{ backgroundColor: white, width: '18%' }} size="small">
            作成日時
          </TableCell>
          <TableCell sx={{ backgroundColor: white, width: '18%' }} size="small">
            更新日時
          </TableCell>
          <TableCell
            sx={{
              backgroundColor: white,
              width: ACTION_COLUMN_WIDTH,
              minWidth: ACTION_COLUMN_WIDTH,
            }}
            size="small"
            aria-label="操作"
          />
        </TableRow>
      </TableHead>
      <TableBody sx={{ backgroundColor: white }}>
        {templates.map((template, index) => (
          <TemplateRow
            key={isTemplateWithId(template) ? template.id : index}
            template={template}
            selectedTemplateIdsState={selectedTemplateIdsState}
          />
        ))}
      </TableBody>
    </Table>
  )
}

const TemplateRow: FC<{
  template: Template
  selectedTemplateIdsState: [Set<number>, Dispatch<SetStateAction<Set<number>>>]
}> = ({
  template,
  selectedTemplateIdsState: [selectedTemplateIds, setSelectedTemplateIds],
}) => {
  const dispatch = useDispatch()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)

  const isGridLayout = template.layoutType === TemplateLayoutTypeEnum.Grid

  const selectTemplate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isTemplateWithId(template)) {
      if (e.target.checked) {
        setSelectedTemplateIds(
          new Set([...Array.from(selectedTemplateIds), template.id]),
        )
        return
      }
      const prunedSelectedTemplateIds = Array.from(selectedTemplateIds).filter(
        selectedTemplateId => selectedTemplateId !== template.id,
      )
      setSelectedTemplateIds(new Set(prunedSelectedTemplateIds))
    }
  }

  const handleMenuOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleMenuClose = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(null)
  }

  const handleDeleteMenuClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setAnchorEl(null)
    if (isTemplateWithId(template)) {
      setDeleteDialogOpen(true)
    }
  }

  const handleDeleteConfirm = () => {
    if (isTemplateWithId(template)) {
      dispatch(deleteTemplate(template.id))
    }
    setDeleteDialogOpen(false)
  }

  const getPlaceNames = (placeNodes: PlaceNode[]) =>
    placeNodes
      .map(({ type, place, placeGroup }) =>
        type === PlaceNodeTypeEnum.PlaceGroup ? placeGroup?.name : place?.name,
      )
      .filter(name => name)
      .join(' ')

  const isChecked = template.id ? selectedTemplateIds.has(template.id) : false

  const deleteTitle = isTemplateWithId(template)
    ? template.hasSchedules
      ? `「${template.name}」はスケジュールに紐いています。削除を実行しますがよろしいですか？`
      : `「${template.name}」の削除を実行しますがよろしいですか？`
    : ''

  return (
    <>
      <TableRow
        hover
        {...(isTemplateWithId(template) && {
          component: Link,
          to: isGridLayout
            ? `/templates/${template.id}/pages/editGridTemplate`
            : `/templates/${template.id}/pages/edit`,
        })}
        sx={{ textDecoration: 'none' }}
      >
        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onChange={selectTemplate}
            size="small"
          />
        </TableCell>
        <TableCell sx={{ wordBreak: 'break-all' }} size="small">
          {template.name}
          <TemplateNameTag
            isAudit={template.isAudit}
            layoutType={template.layoutType}
          />
        </TableCell>
        <TableCell sx={{ wordBreak: 'break-all' }} size="small">
          {getPlaceNames(template.placeNodes ?? [])}
        </TableCell>
        <TableCell size="small">
          {date.formatYYYYMMDDHHMM(template.createdAt)}
        </TableCell>
        <TableCell size="small">
          {date.formatYYYYMMDDHHMM(template.modifiedAt)}
        </TableCell>
        <TableCell
          align="right"
          onClick={e => e.stopPropagation()}
          size="small"
        >
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            aria-label="メニューを開く"
          >
            <MoreHoriz />
          </IconButton>
        </TableCell>
      </TableRow>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <GridToTableMigrationMenuItem
          id={template.id}
          layoutType={template.layoutType}
          onCloseMenu={() => setAnchorEl(null)}
        />
        <MenuItem
          onClick={handleDeleteMenuClick}
          sx={{
            color: semanticColors.colorErrorText,
            '&:hover': {
              backgroundColor: semanticColors.colorErrorBgHover,
              color: semanticColors.colorErrorText,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{deleteTitle}</DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const TemplateNameTag: FC<{
  isAudit?: boolean
  layoutType?: TemplateLayoutTypeEnum
}> = ({ isAudit, layoutType }) => {
  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  if (isFeatureAvailable('improves') && isAudit) {
    return <ImproveOptionTag label="カイゼン" size="small" />
  }
  if (layoutType === TemplateLayoutTypeEnum.Grid) {
    return <GridTemplateTag label="表形式" size="small" />
  }
  return <></>
}

export default TemplateList
